import useAuth from '@app/auth/use-auth';
import { PACKAGING_ITEMS_RATING } from '@app/ccf/company-report/utils';
import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { PickerPeriod } from '@app/components/multi-period-picker';
import { useAccount } from '@shared/api';
import { PackagingActivitiesList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { formatCost, getCurrencySymbol } from '@shared/lib/format';
import { cn, formatSmallNumber } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';
import { PackagingFilterColumn, PackagingFilterOption } from '../filters';

export function usePurchasedPackagingTableColumns({
  filters,
  timePeriods,
}: {
  filters: FilterOptions<PackagingFilterOption[]>;
  timePeriods: PickerPeriod[];
}): ColumnDef<PackagingActivitiesList>[] {
  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);
  const currency = account?.defaultCurrency;

  const columns: ColumnDef<PackagingActivitiesList>[] = [
    {
      accessorKey: 'uuid',
      enableHiding: true,
    },

    {
      accessorKey: 'remoteId',
      header: ({ column }) => <HeaderCell column={column} name="Your ID" />,
      cell: ({ getValue }) => {
        return <Text>{getValue<string>()}</Text>;
      },
      enableHiding: true,
    },
    {
      id: 'packagingName',
      accessorKey: 'packagingName',
      header: ({ column }) => <HeaderCell column={column} name="Name" />,
      cell: ({ getValue }) => (
        <Text className="w-full min-w-52">{getValue<string>()}</Text>
      ),
      enableColumnFilter: true,
      meta: {
        label: 'Search item...',
        variant: 'text',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      accessorKey: 'packagingType',
      accessorFn: (row) => row.packagingType?.name ?? '—',
      header: ({ column }) => (
        <HeaderCell column={column} name="Matched packaging" />
      ),
      cell: ({ getValue }) => {
        const packagingName = getValue<string>();
        return <TextCell text={packagingName} />;
      },
    },
    {
      accessorKey: 'packagingSize',
      accessorFn: (row) => row.packagingSize.description,
      header: ({ column }) => <HeaderCell column={column} name="Size" />,
      cell: ({ getValue }) => {
        return (
          <TextCell
            text={getValue<
              PackagingActivitiesList['packagingSize']['description']
            >()}
          />
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'CarbonIntensityCategory',
      accessorFn: (row) => row.packagingCarbonIntensityCategory,
      header: ({ column }) => (
        <HeaderCell column={column} name="Rating" className="justify-center" />
      ),
      cell: ({ getValue }) => {
        const category =
          getValue<
            PackagingActivitiesList['packagingCarbonIntensityCategory']
          >();
        return (
          <span className="col-span-2">
            <div
              className={cn('size-4 rounded-sm mx-auto')}
              style={{
                backgroundColor: PACKAGING_ITEMS_RATING[category].color,
              }}
            ></div>
          </span>
        );
      },
      enableHiding: true,
    },
    {
      accessorKey: 'emissionFactor',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission Factor"
          numberColumn
          unit={<Unit variant="kgCO2e/unit" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissionFactor = getValue<number>();
        return (
          <TextCell text={formatSmallNumber(emissionFactor)} type="number" />
        );
      },
    },
    {
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Quantity"
          numberColumn
          unit={<Unit variant="units" />}
        />
      ),
      cell: ({ getValue }) => {
        const quantity = getValue<number>();
        return <NumberCell number={quantity} rounded />;
      },
    },
    {
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        return (
          <TextCell
            text={formatSmallNumber(getValue<number>())}
            type="number"
          />
        );
      },
    },
    {
      id: PackagingFilterColumn.Site,
      accessorKey: PackagingFilterColumn.Site,
      enableColumnFilter: true,
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: PackagingFilterColumn.Period,
      accessorKey: PackagingFilterColumn.Period,
      meta: {
        label: 'Time period',
        variant: 'periodPicker',
        periods: timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
      enableColumnFilter: true,
    },
    {
      id: PackagingFilterColumn.Supplier,
      accessorKey: PackagingFilterColumn.Supplier,
      meta: {
        label: 'Supplier',
        variant: 'multiSelect',
        options: filters.suppliers,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
      enableColumnFilter: true,
    },
    {
      id: PackagingFilterColumn.ActivitiesImport,
      accessorKey: PackagingFilterColumn.ActivitiesImport,
      enableColumnFilter: true,
      meta: {
        label: 'Import',
        variant: 'multiSelect',
        options: filters.activitiesImport,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: PackagingFilterColumn.Tag,
      accessorKey: PackagingFilterColumn.Tag,
      enableColumnFilter: true,
      meta: {
        label: 'Tags',
        variant: 'multiSelect',
        options: filters.tags,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
  ];

  if (hasCostFeature) {
    columns.push(
      {
        accessorKey: 'totalSpend',
        header: ({ column }) => (
          <HeaderCell column={column} name="Total spend" numberColumn />
        ),
        cell: ({ getValue }) => {
          const totalSpend = getValue<number | null | undefined>();
          return (
            <TextCell text={formatCost(totalSpend, currency)} type="number" />
          );
        },
      },
      {
        accessorKey: 'costPerUnit',
        header: ({ column }) => (
          <HeaderCell
            column={column}
            name="Average price"
            numberColumn
            unit={<Unit variant={`${getCurrencySymbol(currency)}/unit`} />}
          />
        ),
        cell: ({ getValue }) => {
          const costPerUnit = getValue<number | null | undefined>();
          return (
            <TextCell text={formatCost(costPerUnit, currency)} type="number" />
          );
        },
      }
    );
  }

  return columns;
}
